/**
 * @file guidedMode.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-22
 * @modified 2026-09-22
 * @description Show every label at once, without waiting for it.
 *
 *              WHAT IT IS FOR. Slide-out buttons hold their label back for 1.5s
 *              and the sidebar's sub-item tooltips for 1s, on purpose: a row of
 *              six labels firing as the cursor crosses a header is noise to
 *              somebody who knows the screen. To somebody who does not, it is
 *              the screen declining to say what its buttons do.
 *
 *              TWO WAYS IN, ONE SWITCH. Holding SHIFT answers it for as long as
 *              the key is down; the Guided Mode preference answers it
 *              permanently, for a new starter or for anyone on a screen they
 *              use twice a year. Both set the same attribute, so nothing
 *              downstream has to know which is which — and a preference already
 *              on means SHIFT changes nothing rather than toggling it off.
 *
 *              AN ATTRIBUTE ON <body>, NOT A CONTEXT. The delays live in CSS
 *              (`--slideout-in-delay` in index.css) and in one setTimeout in the
 *              sidebar, so what is needed is something CSS can see and one
 *              handler can read — not a value forty components have to be
 *              handed. Exactly the arrangement `data-readonly` and
 *              `data-rep-mobile` already use.
 */

const ATTR = 'data-guided';

let preferenceOn = false;
let shiftDown = false;

const apply = () => {
  if (typeof document === 'undefined' || !document.body) return;
  if (preferenceOn || shiftDown) document.body.setAttribute(ATTR, 'true');
  else document.body.removeAttribute(ATTR);
};

/** Is everything meant to be shown right now? For the JS-timed tooltip. */
export const guidedNow = () =>
  typeof document !== 'undefined' && document.body?.getAttribute(ATTR) === 'true';

/** The saved preference changed (or has just loaded). */
export function setGuidedPreference(on) {
  preferenceOn = !!on;
  apply();
}

/**
 * Watch for SHIFT. Installed once, at start-up.
 *
 * The blur and visibilitychange handlers are not decoration: alt-tabbing away
 * with SHIFT held means the keyup lands in another window and never arrives
 * here, which would leave every label pinned open until the key was pressed and
 * released again. Releasing on losing focus is the only way to be sure.
 *
 * `getModifierState` rather than `key === 'Shift'` so a SHIFT that goes down
 * during some other keystroke — or is already down when the window regains
 * focus — is seen correctly.
 */
export function installGuidedShiftWatch() {
  if (typeof document === 'undefined') return;

  const sync = (e) => {
    const down = typeof e?.getModifierState === 'function' ? e.getModifierState('Shift') : false;
    if (down === shiftDown) return;
    shiftDown = down;
    apply();
  };

  const release = () => {
    if (!shiftDown) return;
    shiftDown = false;
    apply();
  };

  document.addEventListener('keydown', sync, true);
  document.addEventListener('keyup', sync, true);
  window.addEventListener('blur', release);
  document.addEventListener('visibilitychange', () => { if (document.hidden) release(); });
}
